/**
 * Domain - Organizes the problem space (taxonomy).
 *
 * Domains form a hierarchical taxonomy that organizes Bounded Contexts.
 * Domains do NOT define meaning - they organize contexts that do.
 *
 * Core invariant: Domains organize; contexts define meaning.
 */

import type { BoundedContext } from "./BoundedContext.ts";

/**
 * Reference to an ontology owned by a domain.
 * This is a lightweight reference, not the full ontology schema.
 */
export interface DomainOntologyRef {
    /** Ontology identifier (e.g., 'party-ontology') */
    readonly id: string;

    /** Display label for the ontology */
    readonly label: string;

    /** Namespace IRI of the ontology */
    readonly namespace?: string;
}

export interface Domain {
    /** Unique identifier for the domain */
    readonly id: string;

    /** URI identifier for the domain */
    readonly uri: string;

    /** Display name for the domain */
    readonly name: string;

    /** Natural language definition of the domain */
    readonly definition?: string;

    /** Parent domain in the taxonomy (undefined for root domains) */
    getParent?(): Domain | undefined;

    /** Child domains in the taxonomy */
    getChildren(): Domain[];

    /** Bounded Contexts within this domain */
    getBoundedContexts(): BoundedContext[];

    /** Add a bounded context to this domain */
    addBoundedContext(context: BoundedContext): void;

    /** Ontologies owned by this domain */
    getOntologies(): DomainOntologyRef[];

    /** Add an ontology reference to this domain */
    addOntology(ontology: DomainOntologyRef): void;
}

/**
 * Default implementation of Domain for runtime use.
 */
export class DomainImpl implements Domain {
    readonly id: string;
    readonly uri: string;
    readonly name: string;
    readonly definition?: string;

    private _parent?: Domain;
    private _children: Domain[] = [];
    private _boundedContexts: BoundedContext[] = [];
    private _ontologies: DomainOntologyRef[] = [];

    constructor(params: {
        id: string;
        uri: string;
        name: string;
        definition?: string;
        parent?: Domain;
    }) {
        this.id = params.id;
        this.uri = params.uri;
        this.name = params.name;
        this.definition = params.definition;
        this._parent = params.parent;
    }

    getParent(): Domain | undefined {
        return this._parent;
    }

    getChildren(): Domain[] {
        return this._children;
    }

    getBoundedContexts(): BoundedContext[] {
        return this._boundedContexts;
    }

    addBoundedContext(context: BoundedContext): void {
        // Check by ID to prevent duplicates (same context, different object instances)
        const existingIndex = this._boundedContexts.findIndex(c => c.id === context.id);
        if (existingIndex >= 0) {
            // Replace existing with new instance (e.g., Workspace replaces BoundedContextRef)
            this._boundedContexts[existingIndex] = context;
        } else {
            this._boundedContexts.push(context);
        }
    }

    addChild(child: Domain): void {
        if (!this._children.includes(child)) {
            this._children.push(child);
        }
    }

    getOntologies(): DomainOntologyRef[] {
        return this._ontologies;
    }

    addOntology(ontology: DomainOntologyRef): void {
        if (!this._ontologies.find(o => o.id === ontology.id)) {
            this._ontologies.push(ontology);
        }
    }

    /**
     * Serialize to JSON-LD with per-object @context.
     * Subdomains inherit @context from their parent, so they are serialized without it.
     *
     * @param options.includeContext - Whether to include @context (default: true for root, false for subdomains)
     */
    toJSON(options?: { includeContext?: boolean }): {
        '@context'?: Record<string, unknown>;
        '@type': 'Domain';
        '@id': string;
        name: string;
        definition?: string;
        ontologies: Array<{
            '@type': 'Ontology';
            '@id': string;
            label: string;
        }>;
        contexts: unknown[];
        subdomains: unknown[];
    } {
        const includeContext = options?.includeContext ?? true;

        const base = {
            '@type': 'Domain' as const,
            '@id': this.uri,
            name: this.name,
            ...(this.definition ? { definition: this.definition } : {}),
            ontologies: this._ontologies.map(ont => ({
                '@type': 'Ontology' as const,
                '@id': ont.namespace ?? ont.id,
                label: ont.label
            })),
            contexts: this._boundedContexts.map(ctx => {
                // Use toJSON if available (Workspace), otherwise serialize minimally
                if ('toJSON' in ctx && typeof ctx.toJSON === 'function') {
                    return ctx.toJSON();
                }
                // Fallback for BoundedContextRef or minimal implementations
                return {
                    '@context': { '@vocab': 'http://semantical.io/ontology/core#' },
                    '@type': 'BoundedContext',
                    '@id': ctx.uri,
                    name: ctx.name,
                    ...(ctx.definition ? { definition: ctx.definition } : {})
                };
            }),
            // Subdomains inherit @context from parent, so serialize without it
            subdomains: this._children.map(child => {
                if ('toJSON' in child && typeof child.toJSON === 'function') {
                    return (child as DomainImpl).toJSON({ includeContext: false });
                }
                // Minimal serialization for domains without toJSON (no @context needed)
                return {
                    '@type': 'Domain',
                    '@id': child.uri,
                    name: child.name
                };
            })
        };

        if (includeContext) {
            return {
                '@context': {
                    '@vocab': 'http://semantical.io/ontology/core#',
                    ontologies: { '@id': 'hasOntology', '@type': '@id', '@container': '@set' },
                    contexts: { '@id': 'containsContext', '@type': '@id', '@container': '@set' },
                    subdomains: { '@id': 'hasSubdomain', '@type': '@id', '@container': '@set' }
                },
                ...base
            };
        }

        return base;
    }
}

// Note: Domain instances are now loaded from workspace-config.json
// via WorkspaceConfigLoader. The hardcoded MY_DOMAIN has been removed.
// Use loadInitialConfig() from WorkspaceConfigLoader to get root domains.
